import { ReactNode } from 'react'

import { ToFaResponse, VerifyCreateRequest } from '@/core/interfaces/common'
import {
  CalculateRequest,
  ChangeEmailCodeRequest,
  ChangeEmailRequest,
  ChangeEmailStatusResponse,
  CurrencyCourse,
  ICreateExchangeRequest,
  IExchangeResponse,
} from '@/core/interfaces/exchange'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import AppService from './AppService'

interface AppState {
  sidebar: ReactNode | null
  empty: boolean
  security: ToFaResponse | null
  exchange: IExchangeResponse | null
  currencyDay: CurrencyCourse[]
  changeEmailStatus: ChangeEmailStatusResponse | null
}

const initialState: AppState = {
  sidebar: null,
  empty: false,
  security: null,
  exchange: null,
  currencyDay: [],
  changeEmailStatus: null,
}

export const getVerifyUserThunk = createAsyncThunk(
  'app/getVerifyUserThunk',
  async (data: VerifyCreateRequest, { rejectWithValue }) => {
    try {
      return await AppService.getVerifyUser(data)
    } catch (errors) {
      return rejectWithValue(errors)
    }
  },
)

export const getSecurityInfoThunk = createAsyncThunk(
  'app/getSecurityInfoThunk',
  async (_, { rejectWithValue }) => {
    try {
      return await AppService.getSecurityInfo()
    } catch (errors) {
      return rejectWithValue(errors)
    }
  },
)

export const getPreviewExchangeThunk = createAsyncThunk(
  'app/getPreviewExchangeThunk',
  async (_, { rejectWithValue }) => {
    try {
      return await AppService.getPreviewExchange()
    } catch (errors) {
      return rejectWithValue(errors)
    }
  },
)

export const createExchangeThunk = createAsyncThunk(
  'app/createExchangeThunk',
  async (data: ICreateExchangeRequest, { rejectWithValue }) => {
    try {
      return await AppService.ICreateExchangeRequest(data)
    } catch (errors) {
      return rejectWithValue(errors)
    }
  },
)

export const calculationThunk = createAsyncThunk(
  'app/calculationThunk',
  async (data: CalculateRequest, { rejectWithValue }) => {
    try {
      return await AppService.calculation(data)
    } catch (errors) {
      return rejectWithValue(errors)
    }
  },
)

export const getCourseThunk = createAsyncThunk(
  'app/getCourseThunk',
  async (_, { rejectWithValue }) => {
    try {
      return await AppService.getCourse()
    } catch (errors) {
      return rejectWithValue(errors)
    }
  },
)

export const getDayCourseThunk = createAsyncThunk(
  'app/getDayCourseThunk',
  async (_, { rejectWithValue }) => {
    try {
      return await AppService.getDayCourse()
    } catch (errors) {
      return rejectWithValue(errors)
    }
  },
)

export const changeEmailThunk = createAsyncThunk(
  'app/changeEmailThunk',
  async (data: ChangeEmailRequest, { rejectWithValue }) => {
    try {
      return await AppService.changeEmail(data)
    } catch (errors) {
      return rejectWithValue(errors)
    }
  },
)

export const changeEmailCodeThunk = createAsyncThunk(
  'app/changeEmailCodeThunk',
  async (data: ChangeEmailCodeRequest, { rejectWithValue }) => {
    try {
      return await AppService.changeEmailCode(data)
    } catch (errors) {
      return rejectWithValue(errors)
    }
  },
)

export const changeEmailResentThunk = createAsyncThunk(
  'app/changeEmailResentThunk',
  async (_, { rejectWithValue }) => {
    try {
      return await AppService.changeEmailResent()
    } catch (errors) {
      return rejectWithValue(errors)
    }
  },
)

export const changeEmailStatusThunk = createAsyncThunk(
  'app/changeEmailStatusThunk',
  async (_, { rejectWithValue }) => {
    try {
      return await AppService.changeEmailStatus()
    } catch (errors) {
      return rejectWithValue(errors)
    }
  },
)

export const deleteChangeEmailThunk = createAsyncThunk(
  'app/deleteChangeEmailThunk',
  async (_, { rejectWithValue }) => {
    try {
      return await AppService.deleteChangeEmail()
    } catch (errors) {
      return rejectWithValue(errors)
    }
  },
)

const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    onSetEmpty(state, action) {
      state.empty = action.payload
    },
  },

  extraReducers: builder => {
    builder

      .addCase(getDayCourseThunk.fulfilled, (state, action) => {
        state.currencyDay = action.payload
      })
      .addCase(getDayCourseThunk.rejected, state => {
        state.currencyDay = []
      })

      .addCase(getSecurityInfoThunk.fulfilled, (state, action) => {
        state.security = action.payload
      })
      .addCase(getSecurityInfoThunk.rejected, state => {
        state.security = null
      })

      .addCase(getCourseThunk.fulfilled, (state, action) => {
        state.exchange = action.payload
      })

      .addCase(getCourseThunk.rejected, state => {
        state.exchange = null
      })

      .addCase(changeEmailStatusThunk.fulfilled, (state, action) => {
        state.changeEmailStatus = action.payload
      })
      .addCase(changeEmailStatusThunk.rejected, state => {
        state.changeEmailStatus = null
      })
  },
})

export const { actions, reducer } = appSlice
export const { onSetEmpty } = actions
